import {useMemo} from 'react';
import {useComm} from '../contexts/communication';
import {useAuth} from "../contexts/auth";

function userData({user, token}) {
    const avatar = (user.id % 50).toLocaleString(undefined, { minimumIntegerDigits: 2 });
    return {
        ...user,
        token,
        avatarUrl: `https://js.devexpress.com/Demos/WidgetsGallery/JSDemos/images/employees/${avatar}.png`
    }
}

export function apiUrl(url) {
    return url.startsWith('https://') || url.startsWith('http://')
        ? url
        : `${process.env["REACT_APP_API_URL"].trimEnd('/')}/${url.trimStart('/')}`;
}

export function apiClient(auth, comm)
{
    async function safeRead(response, reader) {
        const backup = response.clone();
        try {
            if (reader === 'text') return await response.text();
            if (reader === 'blob') return await response.blob();
            return await response.json();
        } catch {
            return await backup.text();
        }
    }

    function call(requestOptions)
    {
        const {url, payload, reader, ...restOptions} = requestOptions;
        const resource = apiUrl(url);
        const fetchOptions = {
            ...restOptions,
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${auth?.user?.token}`,
                'ngrok-skip-browser-warning': true,
                ...(payload && { 'Content-Type': 'application/json'}),
                ...restOptions.headers
            },
            ...(payload && { body: JSON.stringify(payload)})
        };
        return new Promise((resolve, reject) =>
            fetch(resource, fetchOptions)
                // got Response, throw if not successful
                .then(async (response) => {
                    const content = await safeRead(response, reader);
                    if (response.ok) resolve(content);
                    else throw content || response.statusText || response.status;
                })
                .catch((err) => comm.error(err))
                .then(reject));
    }

    return {
        get: (url, options) =>
            call({ url, method: 'GET', ...options }),
        post: (url, payload, options) =>
            call({ url, payload, method: 'POST', ...options }),
        put: (url, payload, options) =>
            call({ url, payload, method: 'PUT', ...options }),
        patch: (url, payload, options) =>
            call({ url, payload, method: 'PATCH', ...options }),
        delete: (url, payload, options) =>
            call({ url, payload, method: 'DELETE', ...options }),
        postForm: (url, formData, options) =>
            call({ url, method: 'POST', body: formData, ...options }),
        getObjectUrl: (url, options) =>
            call({ url, method: 'GET', reader: 'blob', ...options }).then(URL.createObjectURL)
    }
}

export const useApi = () => {
    const auth = useAuth();
    const comm = useComm();

    const api = useMemo(() => {
        const client = apiClient(auth, comm);

        return {
            token: auth?.user?.token || null,
            conversation: {
                getById: (id) => client.get(`conversation/${id}`),
                getFullById: (id) => client.get(`conversation/${id}/full`),
                startNew: (modelId, prompt, files, contextId) => client.post('conversation', {modelId, prompt, files, contextId}),
                getLastN: (topN) => client.get(`conversation/last/${isNaN(+topN) ? '' : +topN}`),
                rename: (id, newName) => client.patch(`conversation/${id}/rename`, { summary: newName }),
                delete: (id) => client.delete(`conversation/${id}`)
            },
            gptModel: {
                getActive: () => client.get('gptmodel/active')
            },
            user: {
                login: (login, password) => client.post('user/login', { login, password }),
                getMe: () => client.get('user/me').then(userData),
                getAll: () => client.get('user/all'),
            },
            file: {
                remove: (fileName, conversationId) =>
                    client.delete('file/remove', {fileName, conversationId: +conversationId}),
                uploadSettings: () => client.get('file/upload/settings')
            },
            context: {
                createAndGet: (request) => client.post('context', request),
                getContext: (contextId) => client.get(`context/${contextId}`),
                updateContext: (contextId, request) => client.put(`context/${contextId}`, request),
                deleteContext: (contextId) => client.delete(`context/${contextId}`),
                uploadDocument: (contextId, upFiles) => client.postForm(`context/upload/${contextId}`, upFiles),
                getAll: () => client.get('context'),
                getRelevantDocs: (contextId, prompt) => client.post(`context/relevant-docs/${contextId}`, { prompt }),
                getAccess: (contextId) => client.get(`context/access/${contextId}`),
                upsertAccess: (contextId, accesses) => client.post(`context/access/${contextId}`, accesses),
                getAllDocuments: (contextId) => client.get(`context/documents/${contextId}`),
                deleteDocument: (contextId, docId) => client.delete(`context/documents/${contextId}/${docId}`),
                updateOwnership: (contextId, newUserId) => client.put(`context/ownership/${contextId}/${newUserId}`),
            }

        }
    }, [auth, comm]);
    return api;
};


